import { useState, useEffect, useCallback } from 'react';
import { 
  Package, Search, Filter, Eye, CheckCircle, XCircle, Clock, 
  AlertTriangle, Truck, Building2, Store, ChevronRight, RefreshCw,
  Warehouse as WarehouseIcon, Trash2, RotateCcw, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { transfersAPI, storesAPI, warehousesAPI, stockRequestsAPI } from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  partially_approved: 'bg-teal-100 text-teal-700',
  partial: 'bg-teal-100 text-teal-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  fulfilled: 'bg-green-100 text-green-700'
};

const priorityColors = {
  normal: 'bg-gray-100 text-gray-600',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
  critical: 'bg-red-100 text-red-700'
};

const StockRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [warehouseRequests, setWarehouseRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('warehouse');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Approval dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [warehouseDetailDialogOpen, setWarehouseDetailDialogOpen] = useState(false);
  const [warehouseApprovalDialogOpen, setWarehouseApprovalDialogOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [stores, setStores] = useState([]);
  const [sourceType, setSourceType] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceAvailability, setSourceAvailability] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [approvalItems, setApprovalItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  const { toast } = useToast();

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      
      const response = await transfersAPI.listRequests(params);
      setRequests(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch store requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, priorityFilter, toast]);

  const fetchWarehouseRequests = useCallback(async () => {
    try {
      const params = { page, page_size: 50 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const response = await stockRequestsAPI.list(params);
      // Response is already unwrapped by api.js
      setWarehouseRequests(response.items || []);
    } catch (error) {
      console.error('Failed to fetch warehouse requests:', error);
    }
  }, [page, statusFilter]);

  const fetchSources = useCallback(async () => {
    try {
      const [whRes, stRes] = await Promise.all([
        warehousesAPI.list({ is_active: true }),
        storesAPI.list({ is_active: true })
      ]);
      // API returns array directly, not {data: [...]}
      setWarehouses(Array.isArray(whRes.data) ? whRes.data : whRes.data?.items || []);
      setStores(Array.isArray(stRes.data) ? stRes.data : stRes.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchWarehouseRequests();
    fetchSources();
  }, [fetchRequests, fetchWarehouseRequests, fetchSources]);

  const fetchAvailability = useCallback(async (type, id, items) => {
    if (!type || !id || !items?.length) {
      setSourceAvailability({});
      return;
    }
    try {
      setAvailabilityLoading(true);
      const res = await api.post('/inventory/source-availability', {
        source_type: type,
        source_id: id,
        product_ids: items.map(i => i.product_id)
      });
      setSourceAvailability(res.data?.availability || {});
    } catch (error) {
      setSourceAvailability({});
      toast({ title: 'Error', description: 'Could not load source stock availability', variant: 'destructive' });
    } finally {
      setAvailabilityLoading(false);
    }
  }, [toast]);

  const availableFor = (productId) => sourceAvailability[productId]?.available_qty ?? null;

  const itemShortage = (item) => {
    if (item.removed) return false;
    const avail = availableFor(item.product_id);
    if (avail === null) return false;
    return (item.approved_qty || 0) > avail;
  };

  const activeApprovalItems = () => approvalItems.filter(i => !i.removed && (i.approved_qty || 0) > 0);
  const hasShortage = () => approvalItems.some(itemShortage);

  const toggleRemoveItem = (productId) => {
    setApprovalItems(items => items.map(i => {
      if (i.product_id !== productId) return i;
      const removed = !i.removed;
      return { ...i, removed, approved_qty: removed ? 0 : i.requested_qty };
    }));
  };

  const handleOpenApproval = (request) => {
    setSelectedRequest(request);
    setSourceType('');
    setSourceId('');
    setReviewNotes('');
    setApprovalItems(request.items.map(item => ({
      ...item,
      approved_qty: item.requested_qty,
      removed: false
    })));
    setSourceAvailability({});
    setApprovalDialogOpen(true);
  };

  const handleOpenDetail = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!sourceType || !sourceId) {
      toast({ title: 'Error', description: 'Please select source warehouse/store', variant: 'destructive' });
      return;
    }

    if (!activeApprovalItems().length) {
      toast({ title: 'Error', description: 'At least one item must be approved', variant: 'destructive' });
      return;
    }

    if (hasShortage()) {
      toast({
        title: 'Insufficient stock at source',
        description: 'Reduce the approved quantity or remove the item(s) highlighted in red',
        variant: 'destructive'
      });
      return;
    }

    const missingNote = approvalItems.find(i => i.removed && !(i.hq_notes || '').trim());
    if (missingNote) {
      toast({
        title: 'Note required',
        description: `Add a reason note for the removed item "${missingNote.product_name}"`,
        variant: 'destructive'
      });
      return;
    }

    setProcessing(true);
    try {
      await transfersAPI.approveRequest(selectedRequest.id, {
        source_entity_type: sourceType,
        source_entity_id: sourceId,
        review_notes: reviewNotes,
        items: approvalItems.map(i => ({ ...i, approved_qty: i.removed ? 0 : i.approved_qty }))
      });
      
      toast({ title: 'Success', description: 'Request approved and transfer order created' });
      setApprovalDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to approve request', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reviewNotes.trim()) {
      toast({ title: 'Error', description: 'Please provide rejection reason', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await transfersAPI.rejectRequest(selectedRequest.id, reviewNotes);
      toast({ title: 'Success', description: 'Request rejected' });
      setApprovalDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to reject request', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  // Warehouse request handlers
  const handleOpenWarehouseDetail = (request) => {
    setSelectedRequest(request);
    setWarehouseDetailDialogOpen(true);
  };

  const handleOpenWarehouseApproval = (request) => {
    setSelectedRequest(request);
    setReviewNotes('');
    setApprovalItems(request.items?.map(item => ({
      ...item,
      approved_qty: item.requested_qty,
      removed: false
    })) || []);
    setSourceId('');
    setSourceAvailability({});
    setWarehouseApprovalDialogOpen(true);
  };

  const handleApproveWarehouseRequest = async () => {
    if (!activeApprovalItems().length) {
      toast({ title: 'Error', description: 'At least one item must be approved', variant: 'destructive' });
      return;
    }
    
    if (!sourceId) {
      toast({ title: 'Error', description: 'Please select a source warehouse for dispatch', variant: 'destructive' });
      return;
    }

    if (hasShortage()) {
      toast({
        title: 'Insufficient stock at source warehouse',
        description: 'Reduce the approved quantity or remove the item(s) highlighted in red',
        variant: 'destructive'
      });
      return;
    }

    const missingNote = approvalItems.find(i => i.removed && !(i.hq_notes || '').trim());
    if (missingNote) {
      toast({
        title: 'Note required',
        description: `Add a reason note for the removed item "${missingNote.product_name}"`,
        variant: 'destructive'
      });
      return;
    }

    setProcessing(true);
    try {
      await stockRequestsAPI.approve(selectedRequest.id, {
        hq_notes: reviewNotes,
        source_warehouse_id: sourceId,
        items: approvalItems.map(item => ({
          product_id: item.product_id,
          approved_qty: item.removed ? 0 : item.approved_qty,
          hq_notes: item.hq_notes || null,
          removed: !!item.removed
        }))
      });
      
      toast({ title: 'Success', description: 'Stock request approved and assigned to warehouse for dispatch' });
      setWarehouseApprovalDialogOpen(false);
      setSourceId('');
      fetchWarehouseRequests();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to approve request', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectWarehouseRequest = async () => {
    if (!reviewNotes.trim()) {
      toast({ title: 'Error', description: 'Please provide rejection reason', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await stockRequestsAPI.reject(selectedRequest.id, reviewNotes);
      toast({ title: 'Success', description: 'Stock request rejected' });
      setWarehouseApprovalDialogOpen(false);
      fetchWarehouseRequests();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to reject request', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const updateApprovalQty = (productId, qty) => {
    setApprovalItems(items => items.map(item => 
      item.product_id === productId 
        ? { ...item, approved_qty: Math.max(0, parseFloat(qty) || 0) }
        : item
    ));
  };

  const filteredRequests = requests.filter(req => 
    !search || 
    req.request_number?.toLowerCase().includes(search.toLowerCase()) ||
    req.requester_name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredWarehouseRequests = warehouseRequests.filter(req => 
    !search || 
    req.request_number?.toLowerCase().includes(search.toLowerCase()) ||
    req.warehouse_name?.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = requests.filter(r => ['submitted', 'under_review'].includes(r.status)).length;
  const pendingWarehouseCount = warehouseRequests.filter(r => r.status === 'pending').length;
  const totalPending = pendingCount + pendingWarehouseCount;

  // Get highest priority from items
  const getItemPriority = (items) => {
    if (!items || items.length === 0) return 'normal';
    if (items.some(i => i.priority === 'urgent')) return 'urgent';
    if (items.some(i => i.priority === 'high')) return 'high';
    return 'normal';
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="hq-stock-requests-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            Stock Requests
          </h1>
          <p className="text-gray-500 mt-1">Review and approve stock requests from stores and warehouses</p>
        </div>
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
              <Clock className="w-4 h-4 mr-1" />
              {totalPending} Pending Approval
            </Badge>
          )}
          <Button variant="outline" onClick={() => { fetchRequests(); fetchWarehouseRequests(); }} className="border-gray-300" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="warehouse" className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700" data-testid="tab-warehouse">
            <WarehouseIcon className="w-4 h-4 mr-2" />
            Warehouse Requests
            {pendingWarehouseCount > 0 && (
              <Badge className="ml-2 bg-teal-500 text-white text-xs">{pendingWarehouseCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="store" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700" data-testid="tab-store">
            <Store className="w-4 h-4 mr-2" />
            Store Requests
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-purple-500 text-white text-xs">{pendingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Requests</p>
                <p className="text-2xl font-bold text-gray-800">
                  {activeTab === 'warehouse' ? warehouseRequests.length : requests.length}
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">
                  {activeTab === 'warehouse' ? pendingWarehouseCount : pendingCount}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {activeTab === 'warehouse' 
                    ? warehouseRequests.filter(r => ['approved', 'partial'].includes(r.status)).length
                    : requests.filter(r => r.status === 'approved').length
                  }
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {activeTab === 'warehouse'
                    ? warehouseRequests.filter(r => r.status === 'rejected').length
                    : requests.filter(r => r.status === 'rejected').length
                  }
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder={activeTab === 'warehouse' ? "Search by request number or warehouse..." : "Search by request number or requester..."} 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-300"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-gray-50 border-gray-300" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Status</SelectItem>
                {activeTab === 'warehouse' ? (
                  <>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Warehouse Requests Table */}
      {activeTab === 'warehouse' && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading requests...</div>
            ) : filteredWarehouseRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <WarehouseIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No warehouse stock requests found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-gray-700">Request #</TableHead>
                    <TableHead className="font-semibold text-gray-700">Warehouse</TableHead>
                    <TableHead className="font-semibold text-gray-700">Items</TableHead>
                    <TableHead className="font-semibold text-gray-700">Priority</TableHead>
                    <TableHead className="font-semibold text-gray-700">Required By</TableHead>
                    <TableHead className="font-semibold text-gray-700">Status</TableHead>
                    <TableHead className="font-semibold text-gray-700">Date</TableHead>
                    <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWarehouseRequests.map(request => (
                    <TableRow key={request.id} className="hover:bg-gray-50" data-testid={`warehouse-request-row-${request.id}`}>
                      <TableCell>
                        <span className="font-mono text-sm text-teal-600">{request.request_number}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <WarehouseIcon className="w-4 h-4 text-teal-500" />
                          <div>
                            <p className="font-medium text-gray-800">{request.warehouse_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-800">{request.total_items} items</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityColors[getItemPriority(request.items)] || 'bg-gray-100'}>
                          {getItemPriority(request.items)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {request.required_by_date ? formatDateOnly(request.required_by_date) : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[request.status] || 'bg-gray-100'}>
                          {request.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {formatDateOnly(request.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenWarehouseDetail(request)}
                            className="text-gray-600 hover:text-teal-600"
                            data-testid={`view-btn-${request.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {request.status === 'pending' && (
                            <Button 
                              size="sm"
                              onClick={() => handleOpenWarehouseApproval(request)}
                              className="bg-teal-600 hover:bg-teal-700 text-white"
                              data-testid={`review-btn-${request.id}`}
                            >
                              Review <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Store Requests Table */}
      {activeTab === 'store' && (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No stock requests found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Request #</TableHead>
                  <TableHead className="font-semibold text-gray-700">Requester</TableHead>
                  <TableHead className="font-semibold text-gray-700">Type</TableHead>
                  <TableHead className="font-semibold text-gray-700">Items</TableHead>
                  <TableHead className="font-semibold text-gray-700">Priority</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Date</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => (
                  <TableRow key={request.id} className="hover:bg-gray-50" data-testid={`store-request-row-${request.id}`}>
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600">{request.request_number}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {request.requester_type === 'store' ? (
                          <Store className="w-4 h-4 text-teal-500" />
                        ) : (
                          <Building2 className="w-4 h-4 text-purple-500" />
                        )}
                        <div>
                          <p className="font-medium text-gray-800">{request.requester_name}</p>
                          <p className="text-xs text-gray-500">{request.requester_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 capitalize">
                        {request.request_type?.replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-800">{request.items?.length || 0} items</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={priorityColors[request.priority] || 'bg-gray-100'}>
                        {request.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[request.status] || 'bg-gray-100'}>
                        {request.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {formatDate(request.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDetail(request)}
                          className="text-gray-600 hover:text-blue-600"
                          data-testid={`sr-view-btn-${request.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {['submitted', 'under_review'].includes(request.status) && (
                          <Button 
                            size="sm"
                            onClick={() => handleOpenApproval(request)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`sr-review-btn-${request.id}`}
                          >
                            Review <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && activeTab === 'store' && (
        <div className="flex justify-center gap-2 mt-4">
          <Button 
            variant="outline" 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Package className="w-5 h-5 text-blue-600" />
              Request Details - {selectedRequest?.request_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Requester</Label>
                  <p className="font-medium text-gray-800">{selectedRequest.requester_name}</p>
                  <p className="text-sm text-gray-500">{selectedRequest.requester_type} - {selectedRequest.requester_code}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Badge className={`mt-1 ${statusColors[selectedRequest.status]}`}>
                    {selectedRequest.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Request Type</Label>
                  <p className="font-medium text-gray-800 capitalize">{selectedRequest.request_type?.replace('_', ' ')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Priority</Label>
                  <Badge className={`mt-1 ${priorityColors[selectedRequest.priority]}`}>
                    {selectedRequest.priority}
                  </Badge>
                </div>
              </div>

              {selectedRequest.notes && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-xs text-amber-600">Request Notes</Label>
                  <p className="text-gray-700 mt-1">{selectedRequest.notes}</p>
                </div>
              )}

              {selectedRequest.transfer_order_number && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Label className="text-xs text-emerald-600">Transfer Order Created</Label>
                  <p className="font-mono text-emerald-700">{selectedRequest.transfer_order_number}</p>
                </div>
              )}

              <div>
                <Label className="text-gray-700 font-medium">Requested Items</Label>
                <Table className="mt-2">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Current Stock</TableHead>
                      <TableHead className="text-right">Requested</TableHead>
                      <TableHead className="text-right">Approved</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRequest.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-right text-gray-600">{item.current_stock || 0}</TableCell>
                        <TableCell className="text-right font-medium text-gray-800">{item.requested_qty}</TableCell>
                        <TableCell className="text-right">
                          {item.approved_qty !== null && item.approved_qty !== undefined ? (
                            <span className={item.approved_qty < item.requested_qty ? 'text-amber-600' : 'text-emerald-600'}>
                              {item.approved_qty}
                            </span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Review Request - {selectedRequest?.request_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Requester Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  {selectedRequest.requester_type === 'store' ? (
                    <Store className="w-8 h-8 text-blue-600" />
                  ) : (
                    <Building2 className="w-8 h-8 text-blue-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-800">{selectedRequest.requester_name}</p>
                    <p className="text-sm text-gray-500">
                      {selectedRequest.requester_type} • {selectedRequest.requester_code} • 
                      <span className="capitalize ml-1">{selectedRequest.request_type?.replace('_', ' ')}</span>
                    </p>
                  </div>
                  <Badge className={`ml-auto ${priorityColors[selectedRequest.priority]}`}>
                    {selectedRequest.priority}
                  </Badge>
                </div>
              </div>

              {/* Source Selection */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Label className="text-gray-700 font-medium mb-3 block">Select Source (Where to fulfill from)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">Source Type</Label>
                    <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSourceId(''); setSourceAvailability({}); }}>
                      <SelectTrigger className="bg-white border-gray-300" data-testid="sr-source-type-trigger">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                        <SelectItem value="store">Store</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">
                      {sourceType === 'warehouse' ? 'Select Warehouse' : 'Select Store'}
                    </Label>
                    <Select
                      value={sourceId}
                      onValueChange={(v) => { setSourceId(v); fetchAvailability(sourceType, v, approvalItems); }}
                      disabled={!sourceType}
                    >
                      <SelectTrigger className="bg-white border-gray-300" data-testid="sr-source-trigger">
                        <SelectValue placeholder={sourceType ? `Select ${sourceType}` : 'Select type first'} />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {sourceType === 'warehouse' ? (
                          warehouses.map(wh => (
                            <SelectItem key={wh.id} value={wh.id}>{wh.name} ({wh.code})</SelectItem>
                          ))
                        ) : (
                          stores.filter(s => s.id !== selectedRequest.requester_id).map(st => (
                            <SelectItem key={st.id} value={st.id}>{st.name} ({st.code})</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Items to Approve */}
              <div>
                <Label className="text-gray-700 font-medium mb-2 block">Items (Adjust approved quantities)</Label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Stock at Requester</TableHead>
                        <TableHead className="text-right">Available at Source</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-right w-32">Approve Qty</TableHead>
                        <TableHead className="w-40">Notes</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvalItems.map((item, idx) => {
                        const avail = availableFor(item.product_id);
                        const short = itemShortage(item);
                        return (
                        <TableRow
                          key={idx}
                          className={item.removed ? 'bg-gray-50 opacity-60' : short ? 'bg-red-50' : ''}
                          data-testid={`sr-approval-item-${idx}`}
                        >
                          <TableCell>
                            <p className={`font-medium text-gray-800 ${item.removed ? 'line-through' : ''}`}>{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-right text-gray-600">{item.current_stock || 0}</TableCell>
                          <TableCell className="text-right" data-testid={`sr-available-${idx}`}>
                            {availabilityLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin inline text-gray-400" />
                            ) : avail === null ? (
                              <span className="text-gray-400 text-xs">select source</span>
                            ) : (
                              <span className={short ? 'text-red-600 font-semibold' : 'text-emerald-600 font-medium'}>{avail}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-800">{item.requested_qty}</TableCell>
                          <TableCell className="text-right">
                            <Input 
                              type="number"
                              min="0"
                              max={item.requested_qty}
                              value={item.approved_qty}
                              disabled={item.removed}
                              onChange={(e) => updateApprovalQty(item.product_id, e.target.value)}
                              className={`w-24 text-right bg-white ${short ? 'border-red-400' : 'border-gray-300'}`}
                              data-testid={`sr-approve-qty-${idx}`}
                            />
                            {short && <p className="text-xs text-red-600 mt-1">Only {avail} available</p>}
                          </TableCell>
                          <TableCell>
                            <Input 
                              placeholder={item.removed ? 'Reason (required)' : 'Notes...'}
                              value={item.hq_notes || ''}
                              onChange={(e) => setApprovalItems(items => items.map(i => 
                                i.product_id === item.product_id ? {...i, hq_notes: e.target.value} : i
                              ))}
                              className={`bg-white text-sm ${item.removed && !(item.hq_notes || '').trim() ? 'border-red-400' : 'border-gray-300'}`}
                              data-testid={`sr-item-note-${idx}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={item.removed ? 'Restore item' : 'Remove item'}
                              onClick={() => toggleRemoveItem(item.product_id)}
                              data-testid={`sr-remove-item-${idx}`}
                            >
                              {item.removed
                                ? <RotateCcw className="w-4 h-4 text-gray-500" />
                                : <Trash2 className="w-4 h-4 text-red-500" />}
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Review Notes */}
              <div className="space-y-2">
                <Label className="text-gray-700">Review Notes</Label>
                <Textarea 
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes for approval or rejection reason..."
                  className="bg-white border-gray-300"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setApprovalDialogOpen(false)}
              disabled={processing}
              data-testid="sr-approval-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
              data-testid="sr-approval-reject-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              onClick={handleApprove}
              disabled={processing || !sourceType || !sourceId || hasShortage() || !activeApprovalItems().length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sr-approval-approve-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {processing ? 'Processing...' : 'Approve & Create Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse Request Detail Dialog */}
      <Dialog open={warehouseDetailDialogOpen} onOpenChange={setWarehouseDetailDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <WarehouseIcon className="w-5 h-5 text-teal-600" />
              Stock Request Details - {selectedRequest?.request_number}
            </DialogTitle>
            <DialogDescription>
              View details for this warehouse stock request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <Label className="text-xs text-gray-500">Warehouse</Label>
                  <p className="font-medium">{selectedRequest.warehouse_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Badge className={statusColors[selectedRequest.status]}>
                    {selectedRequest.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Created</Label>
                  <p className="text-sm">{formatDate(selectedRequest.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Required By</Label>
                  <p className="text-sm">{selectedRequest.required_by_date ? formatDateOnly(selectedRequest.required_by_date) : '-'}</p>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-700">Requested Items ({selectedRequest.items?.length || 0})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Requested Qty</TableHead>
                        {['approved', 'partial'].includes(selectedRequest.status) && (
                          <TableHead className="text-right">Approved Qty</TableHead>
                        )}
                        <TableHead>UOM</TableHead>
                        <TableHead>Priority</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRequest.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.product_sku}</p>
                          </TableCell>
                          <TableCell className="text-right font-medium">{item.requested_qty}</TableCell>
                          {['approved', 'partial'].includes(selectedRequest.status) && (
                            <TableCell className="text-right font-medium text-green-600">
                              {item.approved_qty || 0}
                            </TableCell>
                          )}
                          <TableCell>{item.uom}</TableCell>
                          <TableCell>
                            <Badge className={priorityColors[item.priority || 'normal']}>
                              {item.priority || 'normal'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div>
                  <Label className="text-xs text-gray-500">Notes</Label>
                  <p className="text-sm mt-1 p-3 bg-gray-50 rounded-lg">{selectedRequest.notes}</p>
                </div>
              )}
              {selectedRequest.rejection_reason && (
                <div>
                  <Label className="text-xs text-red-500">Rejection Reason</Label>
                  <p className="text-sm mt-1 p-3 bg-red-50 text-red-700 rounded-lg">{selectedRequest.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarehouseDetailDialogOpen(false)}>
              Close
            </Button>
            {selectedRequest?.status === 'pending' && (
              <Button 
                onClick={() => {
                  setWarehouseDetailDialogOpen(false);
                  handleOpenWarehouseApproval(selectedRequest);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Review & Approve
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse Approval Dialog */}
      <Dialog open={warehouseApprovalDialogOpen} onOpenChange={setWarehouseApprovalDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              Review Stock Request - {selectedRequest?.request_number}
            </DialogTitle>
            <DialogDescription>
              Review and approve/reject the warehouse stock request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Warehouse Info */}
              <div className="flex items-center gap-3 bg-teal-50 rounded-lg p-4">
                <WarehouseIcon className="w-8 h-8 text-teal-600" />
                <div>
                  <p className="font-medium text-gray-800">{selectedRequest.warehouse_name}</p>
                  <p className="text-sm text-gray-500">Required by: {selectedRequest.required_by_date ? formatDateOnly(selectedRequest.required_by_date) : 'Not specified'}</p>
                </div>
              </div>

              {/* Source Warehouse Selection */}
              <div className="bg-amber-50 rounded-lg p-4">
                <Label className="text-sm font-semibold text-amber-800">Select Source Warehouse *</Label>
                <p className="text-xs text-amber-600 mb-2">Choose which warehouse will dispatch the stock</p>
                <Select 
                  value={sourceId} 
                  onValueChange={(v) => { setSourceId(v); fetchAvailability('warehouse', v, approvalItems); }}
                >
                  <SelectTrigger className="bg-white" data-testid="wh-source-trigger">
                    <SelectValue placeholder="Select source warehouse..." />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.name}{wh.id === selectedRequest?.warehouse_id ? ' (same warehouse)' : ''}
                      </SelectItem>
                    ))}
                    {warehouses.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No active warehouses found</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Approval Items */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-700">Set Approved Quantities</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead className="text-right">Available at Source</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-right">Approve Qty</TableHead>
                        <TableHead className="w-40">Notes</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvalItems.map((item, idx) => {
                        const avail = availableFor(item.product_id);
                        const short = itemShortage(item);
                        return (
                        <TableRow
                          key={idx}
                          className={item.removed ? 'bg-gray-50 opacity-60' : short ? 'bg-red-50' : ''}
                          data-testid={`wh-approval-item-${idx}`}
                        >
                          <TableCell>
                            <p className={`font-medium ${item.removed ? 'line-through' : ''}`}>{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.product_sku}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityColors[item.priority || 'normal']}>
                              {item.priority || 'normal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" data-testid={`wh-available-${idx}`}>
                            {availabilityLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin inline text-gray-400" />
                            ) : avail === null ? (
                              <span className="text-gray-400 text-xs">select source</span>
                            ) : (
                              <span className={short ? 'text-red-600 font-semibold' : 'text-emerald-600 font-medium'}>{avail}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">{item.requested_qty}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max={item.requested_qty}
                              value={item.approved_qty}
                              disabled={item.removed}
                              onChange={(e) => updateApprovalQty(item.product_id, e.target.value)}
                              className={`w-24 text-right ${short ? 'border-red-400' : ''}`}
                              data-testid={`wh-approve-qty-${idx}`}
                            />
                            {short && <p className="text-xs text-red-600 mt-1">Only {avail} available</p>}
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder={item.removed ? 'Reason (required)' : 'Notes...'}
                              value={item.hq_notes || ''}
                              onChange={(e) => setApprovalItems(items => items.map(i =>
                                i.product_id === item.product_id ? {...i, hq_notes: e.target.value} : i
                              ))}
                              className={`text-sm ${item.removed && !(item.hq_notes || '').trim() ? 'border-red-400' : ''}`}
                              data-testid={`wh-item-note-${idx}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={item.removed ? 'Restore item' : 'Remove item'}
                              onClick={() => toggleRemoveItem(item.product_id)}
                              data-testid={`wh-remove-item-${idx}`}
                            >
                              {item.removed
                                ? <RotateCcw className="w-4 h-4 text-gray-500" />
                                : <Trash2 className="w-4 h-4 text-red-500" />}
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm text-gray-700">Notes / Reason (required for rejection)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes for the warehouse..."
                  className="mt-2 bg-white"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setWarehouseApprovalDialogOpen(false)} data-testid="wh-approval-cancel-btn">
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRejectWarehouseRequest}
              disabled={processing}
              data-testid="wh-approval-reject-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              onClick={handleApproveWarehouseRequest}
              disabled={processing || !sourceId || hasShortage() || !activeApprovalItems().length}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="wh-approval-approve-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {processing ? 'Processing...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockRequestsPage;
